import { useEffect, useState } from "react";
import {
  getMetaConsent,
  initializeMetaPixel,
  persistMetaConsent,
  trackMetaEvent,
  type MetaConsent,
} from "./meta";

export function MetaTrackingChoice() {
  const [choice, setChoice] = useState<MetaConsent>(() => getMetaConsent());
  const [open, setOpen] = useState(choice === "undecided");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const update = (event: Event) => setChoice((event as CustomEvent<MetaConsent>).detail);
    window.addEventListener("vessa:meta-consent", update);
    return () => window.removeEventListener("vessa:meta-consent", update);
  }, []);

  const choose = async (next: Exclude<MetaConsent, "undecided">) => {
    setSaving(true);
    setError("");
    try {
      await persistMetaConsent(next);
      setChoice(next);
      setOpen(false);
      if (next === "accepted") {
        initializeMetaPixel();
        void trackMetaEvent("PageView");
      }
    } catch {
      setError("Your privacy choice could not be saved. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button type="button" className="tracking-choice-button" onClick={() => setOpen(true)}>
        Privacy choices
      </button>
    );
  }

  return (
    <aside className="tracking-choice" role="dialog" aria-label="Marketing tracking choice">
      <div>
        <strong>Choose marketing tracking</strong>
        <p>
          With permission, Vessa shares generic funnel actions with Meta to measure ads. Quiz
          answers, medications, symptoms, measurements, and health details are never shared.
        </p>
        {choice !== "undecided" && <small>Current choice: {choice}.</small>}
        {error && <small role="alert">{error}</small>}
      </div>
      <div className="tracking-choice-actions">
        <button type="button" className="tracking-reject" disabled={saving} onClick={() => void choose("rejected")}>
          Reject
        </button>
        <button type="button" className="tracking-accept" disabled={saving} onClick={() => void choose("accepted")}>
          Allow Meta measurement
        </button>
      </div>
    </aside>
  );
}
